<template>
  <Dialog
    v-model:visible="showModalRef"
    :close-on-escape="store.getters.dialogsCloseOnEscape"
    :dismissableMask="store.getters.dialogsDismissableMask"
    :modal="store.getters.dialogsModal"
  >
    <template #header>
      {{ title }}
      <i
        class="custom-close-dialog-button cursor-pointer p-2 pi pi-times"
        @click="closeModal()"
      ></i>
    </template>

    <div
      :class="[loadingForm ? 'flex' : 'hidden']"
      class="justify-content-center align-items-center h-full"
    >
      <i class="pi pi-spinner pi-spin" style="font-size: 2rem"></i>
    </div>

    <Form
      ref="formRef"
      v-bind="formConfigRef"
      :class="[loadingForm ? 'hidden' : 'flex']"
    />

    <template #footer>
      <div class="col-12">
        <div class="flex justify-content-between flex-wrap gap-3">
          <Button label="Cancelar" severity="secondary" @click="closeModal()" />

          <div class="flex flex-wrap gap-3">
            <Button
              label="Comprobar conexión"
              severity="warning"
              @click="testConnection()"
              :loading="loading || loadingForm"
              loading-icon="pi pi-spinner"
            />

            <Button
              :label="title"
              @click="save"
              :loading="loading || loadingForm"
              loading-icon="pi pi-spinner"
            />
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { computed, ref } from "vue";
import { useToast } from "primevue/usetoast";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import { useStore } from "vuex";
import { useConfirm } from "primevue/useconfirm";
import { Form, useImmer, useValidations } from "@sttedra_ui/framework";

import useFormConfig, {
  databaseTypes,
  ddbbTypes,
} from "@/pages/_root/data-sources-dialog/data-sources-dialog-form.view-config.js"; // eslint-disable-line
import {
  dataSourcesCreate,
  dataSourcesUpdate,
  dataSourcesCheckService,
} from "@/api/data-sources/data-sources.api.js";
import {
  checkFormHasData,
  handleShowConfirm,
} from "@/composables/_commons/helper.js";

// #region Props, Emits
const emit = defineEmits(["saved"]);
// #endregion

// #region Params, Ref, Reactives, Computed
const loadingForm = ref(false);
const loading = ref(false);
const store = useStore();
const confirm = useConfirm();
const { required } = useValidations();
const toast = useToast();
const showModalRef = ref(false);
const formRef = ref(null);
const formConfig = ref(null);
const { ref: formConfigRef, update: updateFormConfig } = useImmer(formConfig);
const register = ref(null);
const actionValue = ref(null);
const passwordRules = ref(null);

const title = computed(() => {
  if (actionValue.value == "copy") {
    return "Clonar fuente de datos";
  } else {
    return (register.value?.idFuente ? "Editar" : "Crear") + " fuente de datos";
  }
});
// #endregion

// #region Functions
async function showModal(data, action = null) {
  loadingForm.value = true;

  await databaseTypes();

  passwordRules.value = data != null ? null : { required };
  formConfig.value = new useFormConfig(null, passwordRules.value);

  actionValue.value = action;
  showModalRef.value = true;
  register.value = null;

  passwordRules.value = data != null ? null : { required };

  if (data != null) {
    register.value = data;

    const object = Object.keys(data).map((key) => {
      return {
        name: key,
        value: data[key],
      };
    });

    formConfig.value = new useFormConfig(object, passwordRules.value);
  }

  loadingForm.value = false;
}

async function validateForm() {
  const invalid = !(await formRef.value?.v$Ref.$validate());

  return invalid;
}

function getFormValues() {
  return { ...formRef.value.state$$.values };
}

async function save() {
  const editingRegister =
    register.value?.idFuente && actionValue.value != "copy";

  let passwordRequired = !editingRegister;

  if (actionValue.value == "copy") {
    passwordRequired = false;
  }

  await changePasswordRules(passwordRequired);

  if (await validateForm()) return;

  loading.value = true;

  const data = getFormValues();

  if (data.bbddActiva == undefined) {
    data.bbddActiva = false;
  }

  if (actionValue.value == "copy") {
    data.idFuenteDatosClonacion = register.value.idFuente;
  }

  let response = null;

  if (editingRegister) {
    data.id = register.value.idFuente;
    response = await dataSourcesUpdate(register.value?.idFuente, data);
  } else {
    response = await dataSourcesCreate(data);
  }

  if (response.status == 200) {
    toast.add({
      severity: "success",
      summary: `Registro ${register.value?.idFuente ? "editado" : "creado"}`,
      detail: `${
        register.value?.idFuente ? "Edición" : "Creación"
      } realizada correctamente`,
      life: 3000,
    });

    showModalRef.value = false;

    emit("saved", response.data);
  } else {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: `No se ha
      podido realizar la ${register.value?.idFuente ? "edición" : "creación"}`,
      life: 3000,
    });
  }

  loading.value = false;
}

// Test the conection with the form data
async function testConnection() {
  await changePasswordRules(true);

  if (await validateForm()) return;

  loading.value = true;

  const data = getFormValues();

  // Find data.idBbdd in databaseTypes, then get the tipo field and set it to "data" as "tipoBbdd"
  ddbbTypes.forEach((databaseType) => {
    if (databaseType.idBbdd == data.idBbdd) {
      data.tipoBbdd = databaseType.tipo;
    }
  });

  // If the form is valid, test the connection
  toast.add({
    severity: "info",
    summary: "Comprobando conexión",
    detail: "Esto puede tardar unos segundos",
    closable: true,
    life: 3000,
  });

  dataSourcesCheckService(data).then((response) => {
    if (response.data === true) {
      toast.add({
        severity: "success",
        summary: "Conexión realizada",
        detail: "La conexión se ha realizado correctamente",
        closable: true,
        life: 3000,
      });
    } else {
      toast.add({
        severity: "error",
        summary: "Datos incorrectos",
        detail: "No se ha podido realizar la conexión",
        closable: true,
        life: 3000,
      });
    }

    loading.value = false;
  });
}

async function changePasswordRules(passwordRequired) {
  await updateFormConfig((config) => {
    const items = config.items;

    items.forEach((item) => {
      if (item.field == "contrasena") {
        item.rules = passwordRequired ? { required } : {};
      }
    });

    return config;
  });
}

function closeModal() {
  let showConfirm = false;

  if (checkFormHasData({ ...formRef.value.state$$.values })) {
    showConfirm = true;
  }

  if (showConfirm) {
    handleShowConfirm(confirm, showModalRef);
  } else {
    showModalRef.value = false;
  }
}
// #endregion

// #region Effects, Hooks
// #endregion

// #region Provide, Expose
defineExpose({
  showModal,
});
// #endregion
</script>
